import {useEffect} from 'react';
import {useTranslation} from "react-i18next";
import {Listbox, ListboxItem} from "@nextui-org/react";
import Header from "../../reusable/Header";
import {ArrowForward} from "../../icons/ArrowForward";
import {IdIcon} from "../../icons/IdIcon";
import PoweredBy from "../../reusable/PoweredBy";
import CountryLabel from "../../reusable/CountryLabel";
import {useDispatch, useSelector} from "react-redux";
import {getConfiguredDocuments} from "../../../store/verification.slice";
import { IS_DEV } from "../../utils";

const mockDocumentTypes = [
    { key: 'passport', name: 'PASSPORT', sides: ['FRONT'] },
    { key: 'ninIdSlip', name: 'NIN_ID_SLIP', sides: ['FRONT'] },
    { key: 'nationalId', name: 'NATIONAL_ID', sides: ['FRONT', 'BACK'] },
    { key: 'driversLicense', name: 'DRIVERS_LICENSE', sides: ['FRONT', 'BACK'] },
];

const DocumentTypeSelect = ({onSelect, goback, country, stepNumber, totalSteps}) => {
    const { t } = useTranslation();
    const { credentials, documentTypes } = useSelector((store) => store.verification);
    const dispatch = useDispatch();

    const params = new URLSearchParams(window.location.search);
    const clientId = params.get('clientId');
    const flowId = params.get('flowId');
    const isDebug = IS_DEV && params.get('debug') === 'true';

    const displayedDocumentTypes = isDebug
        ? mockDocumentTypes.map((doc) => ({ ...doc, label: t(`documentUpload.typeSelect.mockTypes.${doc.key}`) }))
        : documentTypes;

    useEffect(() => {
        if (!isDebug) {
            dispatch(getConfiguredDocuments(flowId, country?.code, stepNumber, clientId, credentials.accessToken));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, dispatch]);

    const handleSubmit = (selected) => {
        onSelect(selected.values().next().value);
    };

    return (
        <div className="flex h-full flex-col">
            <Header goBack={goback}/>

            <div className="px-4 pb-3 flex flex-col items-center gap-1">
                <div className="heading text-xl text-center">{t("documentUpload.typeSelect.heading")}</div>
                <CountryLabel name={country?.name} flag={country?.flag} code={country?.code}/>
                {totalSteps > 1 && (
                    <div
                        className="text-xs font-semibold uppercase tracking-widest mt-1"
                        style={{color: "var(--urtentic-sdk-body, #737373)"}}
                    >
                        {t("documentUpload.typeSelect.step", { step: stepNumber, total: totalSteps })}
                    </div>
                )}
            </div>

            {/* Scrollable list */}
            <div className="flex-grow overflow-auto px-4">
                {!displayedDocumentTypes && (
                    <div className="flex justify-center items-center h-32">
                        <span className="desc text-sm">{t("documentUpload.typeSelect.loading")}</span>
                    </div>
                )}
                {displayedDocumentTypes && (
                    <Listbox
                        aria-label="Document type selection"
                        variant="flat"
                        disallowEmptySelection
                        selectionMode="single"
                        onSelectionChange={handleSubmit}
                    >
                        {displayedDocumentTypes.map((doc) => {
                            const sideCount = doc.sides?.length ?? 1;
                            const sideLabel = sideCount === 1
                                ? t("documentUpload.typeSelect.singlePage")
                                : t("documentUpload.typeSelect.multiSide", { count: sideCount });
                            return (
                                <ListboxItem
                                    className="py-3"
                                    key={`${JSON.stringify(doc)}`}
                                    showDivider
                                    description={<span style={{color: "var(--urtentic-sdk-body, #737373)"}}>{sideLabel}</span>}
                                    startContent={<IdIcon height={24} width={24} stroke="var(--urtentic-sdk-primary, #255CFF)"/>}
                                    endContent={<ArrowForward/>}
                                >
                                    <span style={{color: "var(--urtentic-sdk-body, #737373)"}}>{doc.label}</span>
                                </ListboxItem>
                            );
                        })}
                    </Listbox>
                )}
            </div>

            <div className="flex justify-center p-2">
                <PoweredBy/>
            </div>
        </div>
    );
};

export default DocumentTypeSelect;
